import { useState, useEffect } from 'react';

interface QuantityModalProps {
  isOpen: boolean;
  individualesCount: number;
  duplasCount: number;
  triosCount: number;
  onConfirm: (quantities: { individuales: number; duplas: number; trios: number }) => void;
  onCancel: () => void;
}

export default function QuantityModal({
  isOpen,
  individualesCount,
  duplasCount,
  triosCount,
  onConfirm,
  onCancel,
}: QuantityModalProps) {
  const [individualesPerList, setIndividualesPerList] = useState<string>('10');
  const [duplasPerList, setDuplasPerList] = useState<string>('8');
  const [triosPerList, setTriosPerList] = useState<string>('6');

  useEffect(() => {
    if (isOpen) {
      setIndividualesPerList('10');
      setDuplasPerList('8');
      setTriosPerList('6');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const ind = Math.max(1, parseInt(individualesPerList) || 1);
    const dup = Math.max(1, parseInt(duplasPerList) || 1);
    const tri = Math.max(1, parseInt(triosPerList) || 1);
    onConfirm({ individuales: ind, duplas: dup, trios: tri });
  };

  const typeCount = [individualesCount > 0, duplasCount > 0, triosCount > 0].filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-sliders-2-line text-xl text-white"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Cantidad por Lista</h2>
              <p className="text-indigo-200 text-xs mt-0.5">
                Indicá cuántos registros querés en cada lista
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-4">
          {typeCount === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              No hay datos cargados.
            </p>
          )}

          {individualesCount > 0 && (
            <div className="flex items-center gap-4 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <div className="w-10 h-10 flex items-center justify-center bg-indigo-600 rounded-lg shrink-0">
                <i className="ri-user-line text-white text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-indigo-900">Individuales</p>
                <p className="text-xs text-indigo-500">{individualesCount} en total</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIndividualesPerList(v => String(Math.max(1, (parseInt(v) || 1) - 1)))}
                  className="w-7 h-7 flex items-center justify-center bg-white border border-indigo-200 rounded-lg text-indigo-600 hover:bg-indigo-100 transition-colors cursor-pointer font-bold"
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  max={individualesCount}
                  value={individualesPerList}
                  onChange={e => setIndividualesPerList(e.target.value)}
                  className="w-14 text-center text-sm font-bold text-indigo-800 border border-indigo-200 rounded-lg py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <button
                  type="button"
                  onClick={() => setIndividualesPerList(v => String(Math.min(individualesCount, (parseInt(v) || 1) + 1)))}
                  className="w-7 h-7 flex items-center justify-center bg-white border border-indigo-200 rounded-lg text-indigo-600 hover:bg-indigo-100 transition-colors cursor-pointer font-bold"
                >
                  +
                </button>
              </div>
              <span className="text-xs text-indigo-500 whitespace-nowrap">/ lista</span>
            </div>
          )}

          {duplasCount > 0 && (
            <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg shrink-0">
                <i className="ri-group-line text-white text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-emerald-900">Duplas</p>
                <p className="text-xs text-emerald-500">{duplasCount} en total</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDuplasPerList(v => String(Math.max(1, (parseInt(v) || 1) - 1)))}
                  className="w-7 h-7 flex items-center justify-center bg-white border border-emerald-200 rounded-lg text-emerald-600 hover:bg-emerald-100 transition-colors cursor-pointer font-bold"
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  max={duplasCount}
                  value={duplasPerList}
                  onChange={e => setDuplasPerList(e.target.value)}
                  className="w-14 text-center text-sm font-bold text-emerald-800 border border-emerald-200 rounded-lg py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                <button
                  type="button"
                  onClick={() => setDuplasPerList(v => String(Math.min(duplasCount, (parseInt(v) || 1) + 1)))}
                  className="w-7 h-7 flex items-center justify-center bg-white border border-emerald-200 rounded-lg text-emerald-600 hover:bg-emerald-100 transition-colors cursor-pointer font-bold"
                >
                  +
                </button>
              </div>
              <span className="text-xs text-emerald-500 whitespace-nowrap">/ lista</span>
            </div>
          )}

          {triosCount > 0 && (
            <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-lg shrink-0">
                <i className="ri-team-line text-white text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">Tríos</p>
                <p className="text-xs text-amber-500">{triosCount} en total</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTriosPerList(v => String(Math.max(1, (parseInt(v) || 1) - 1)))}
                  className="w-7 h-7 flex items-center justify-center bg-white border border-amber-200 rounded-lg text-amber-600 hover:bg-amber-100 transition-colors cursor-pointer font-bold"
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  max={triosCount}
                  value={triosPerList}
                  onChange={e => setTriosPerList(e.target.value)}
                  className="w-14 text-center text-sm font-bold text-amber-800 border border-amber-200 rounded-lg py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <button
                  type="button"
                  onClick={() => setTriosPerList(v => String(Math.min(triosCount, (parseInt(v) || 1) + 1)))}
                  className="w-7 h-7 flex items-center justify-center bg-white border border-amber-200 rounded-lg text-amber-600 hover:bg-amber-100 transition-colors cursor-pointer font-bold"
                >
                  +
                </button>
              </div>
              <span className="text-xs text-amber-500 whitespace-nowrap">/ lista</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="flex-1 py-3 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-play-circle-line text-base"></i>
            Generar Listas
          </button>
        </div>
      </div>
    </div>
  );
}
